<template>
	<div>
		<div>
			<TabMenu class="removeBorderWidth" :model="items" />
		</div>
		<div class="p-mt-2 p-mb-2">
			<router-view />
		</div>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import useSqueezeStore from "@/apps/squeeze/store";
import TabMenu from "@/components/DexTabMenu.vue";
import {MenuItem} from "@/shims-prime-vue";
import {ClientManager} from "@/singletons/ClientManager";
import {LLM_EXTRACTION_SETTING, useDocumentClassSettings} from "@/composables/useDocumentClassSettings";

export default defineComponent({
	name: 'DocumentClassView',
	components: {
		TabMenu,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** Service for Document-Classes */
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		/**
		 * Settings of the current document class. The sub views publish them whenever they
		 * load or change a setting, so the tabs below follow without a page reload.
		 */
		const {documentClassSettings, setDocumentClassSettings} = useDocumentClassSettings();

		/** Is the current document class one of the DEXPRO solution classes? */
		const isSolutionClass = ref<boolean>(false);

		/** Is the LLMExtraction setting active for the current document class? */
		const llmExtractionActive = computed<boolean>(() => {
			return documentClassSettings.value
				.some(setting => setting.settingName === LLM_EXTRACTION_SETTING && setting.value === 'true');
		});

		/**
		 * The System Prompt tab exists only with the llmExtraction
		 * FeatureFlag and only for document classes that actually use the LLM extraction.
		 */
		const showSystemPromptTab = computed<boolean>(() => {
			return Boolean(store.state.featureSet.llmExtraction)
				&& llmExtractionActive.value;
		});

		/** Loads what the System Prompt tab needs: the solution class flag, the class settings and user need admin permissions */
		const getSystemPromptTabState = () => {
			if (!props.documentClassId || !store.state.featureSet.llmExtraction || !store.state.isAdminUser) {
				return;
			}

			documentClassService.getDocumentClassById(props.documentClassId)
				.then(data => {
					isSolutionClass.value = data.isSolutionClass === true;
				})
				.catch(() => {
					// Without the flag the tab is disabled, because DEXPRO's predefined classes use their own system prompt and can not be modified by users
					isSolutionClass.value = true;
				});

			documentClassService.getDocumentClassSettings(props.documentClassId)
				.then(settings => {
					setDocumentClassSettings(settings);
				})
				.catch(() => {
					// Without the settings the LLM extraction counts as inactive.
					setDocumentClassSettings([]);
				});
		}

		/** List of all Tab-Items */
		const items = computed(() => {
			const itemsList: MenuItem[] = [
				{label: t('Squeeze.DocumentClasses.General'), to: { name: 'DocumentClassSubView', params: { documentClassId: props.documentClassId }}},
			]

			if (props.documentClassId) {
				itemsList.push({
					label: t('Squeeze.DocumentClasses.Fields'),
					to: { name: 'DocumentClassFieldGroupsSubView', params: { documentClassId: props.documentClassId }},
				});
				itemsList.push({
					label: t('Squeeze.DocumentClasses.Tables'),
					to: { name: 'DocumentClassTablesSubView', params: { documentClassId: props.documentClassId }},
				});
				itemsList.push({
					label: t('Squeeze.DocumentClasses.ExportInterfaces'),
					to: { name: 'DocumentClassExportInterfaceSubView', params: { documentClassId: props.documentClassId }},
				});
				itemsList.push({
					label: t('Squeeze.DocumentClasses.Attributes'),
					to: { name: 'DocumentClassAttributesSubView', params: { documentClassId: props.documentClassId }},
				});
				itemsList.push({
					label: t('Squeeze.DocumentClasses.DeleteReasons'),
					to: { name: 'DocumentClassDeleteReasonsSubView', params: { documentClassId: props.documentClassId }},
				});
				itemsList.push({
					label: t('Squeeze.System.Translations'),
					to: { name: 'DocumentClassTranslationsSubView', params: { documentClassId: props.documentClassId }},
				});

				if (showSystemPromptTab.value) {
					itemsList.push({
						label: t('Squeeze.DocumentClasses.SystemPrompt'),
						to: { name: 'DocumentClassSystemPromptSubView', params: { documentClassId: props.documentClassId }},
						// Solution class prompts are modified by DEXPRO, the tab
						// stays visible but cannot be opened and tooltip is shown
						disabled: isSolutionClass.value,
						tooltip: isSolutionClass.value ? t('Squeeze.DocumentClasses.SystemPromptNotForSolutionClasses') : undefined,
					});
				}

				if (store.state.featureSet.xmlEditor) {
					itemsList.push({
						label: 'XML Mapper',
						to: { name: 'MapperView', params: { documentClassId: props.documentClassId }},
					});
				}
			}

			return itemsList;
		});

		onMounted(() => {
			getSystemPromptTabState();
		})

		watch(() => props.documentClassId, () => {
			getSystemPromptTabState();
		})

		return {
			t,
			items,
			isSolutionClass,
			showSystemPromptTab,
			getSystemPromptTabState,
		};
	},
});

</script>

<style lang="scss" scoped>

.removeBorderWidth  {
	border-bottom-width: 0;
}

</style>
